/**
 * ConnectionQualityGauge — a small signal-strength gauge for a video tile
 * (007-connection-quality).
 *
 * Presentational and pure: output depends only on `level`. No store access, no
 * timers, no stats. Renders QUALITY_TOTAL_BARS bars with the first
 * QUALITY_BARS[level] filled — the filled-bar COUNT is the greyscale-safe level
 * cue (FR-008); colour is supplementary. Exposes role="img" + an i18n
 * aria-label/title so assistive tech announces the level.
 */
import { useTranslation } from 'react-i18next'
import {
  QUALITY_BARS,
  QUALITY_TOTAL_BARS,
  QUALITY_I18N_KEY,
  type QualityLevel,
} from '../room/connectionQuality.js'
import styles from './ConnectionQualityGauge.module.css'

interface ConnectionQualityGaugeProps {
  level: QualityLevel
  /** Optional positioning hook supplied by the parent tile. */
  className?: string | undefined
}

export function ConnectionQualityGauge({ level, className }: ConnectionQualityGaugeProps) {
  const { t } = useTranslation()

  const filled = QUALITY_BARS[level]
  const levelLabel = t(QUALITY_I18N_KEY[level])
  const ariaLabel = t('tile.signal.label', { level: levelLabel })

  const bars = Array.from({ length: QUALITY_TOTAL_BARS }, (_, i) => i < filled)

  return (
    <div
      data-testid="tile-quality"
      data-level={level}
      className={`${styles.gauge} ${styles[level]} ${className ?? ''}`}
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {bars.map((isFilled, i) => (
        <span
          key={i}
          aria-hidden="true"
          data-testid="quality-bar"
          data-filled={isFilled ? 'true' : 'false'}
          className={`${styles.bar} ${isFilled ? styles.filled : styles.empty}`}
        />
      ))}
    </div>
  )
}
